import { ArrowLeft, Ban, Building2, KeyRound, RefreshCw, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest } from "../../lib/api";
import { formatCalendarDate, formatTimestamp, money } from "../../lib/format";
import { useAdminUser, useInvalidateAll } from "../../lib/queries";
import type { ApiEnvelope, AdminSpeiDestination, AdminUserDetail, Wallet } from "../../types";
import { AdminPhotoField, AuditList, ConfirmButton, Feedback, Field, KeyValues, Pill, useAction } from "./shared";
import { KuspitClosureCard } from "./KuspitClosureCard";

export function AdminUserPage() {
  const { id } = useParams();
  const detail = useAdminUser(id);
  usePageHeader(detail.data?.user.fullName ?? "Usuario", detail.data?.user.email);
  if (detail.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (detail.isError || !detail.data) return <div className="page-state error"><strong>No encontramos este usuario</strong><Link className="secondary-button" to="/admin/usuarios">Volver</Link></div>;
  const data = detail.data;
  return (
    <div className="page-container">
      <Link to="/admin/usuarios" className="back-button"><ArrowLeft size={16} aria-hidden="true" /> Todos los usuarios</Link>
      <div className="admin-two-col">
        <div className="admin-stack">
          <ProfileForm data={data} />
          <TripForm data={data} />
          <KuspitCard data={data} />
          {(data.user.role === "admin" || data.user.role === "lender" || data.user.mfaEnrolledAt) && <StaffMfaCard data={data} />}
          <UserDangerZone data={data} />
        </div>
        <div className="admin-stack">
          {data.wallets.length === 0 && <section className="card"><span className="rail-title">Wallets</span><p className="rail-empty">Este cliente aún no ha abierto una wallet. Los términos del viaje se mostrarán en su Inicio y Perfil de todos modos.</p></section>}
          {data.wallets.map((wallet) => <WalletForm key={wallet.id} wallet={wallet} />)}
          <section className="card">
            <span className="rail-title">Bitácora del cliente</span>
            <AuditList events={data.audit} />
          </section>
        </div>
      </div>
    </div>
  );
}

function ProfileForm({ data }: { data: AdminUserDetail }) {
  const { user } = data;
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [fullName, setFullName] = useState(user.fullName);
  const [kycStatus, setKyc] = useState(user.kycStatus);
  const [status, setStatus] = useState(user.status);
  const [role, setRole] = useState(user.role);
  useEffect(() => { setFullName(user.fullName); setKyc(user.kycStatus); setStatus(user.status as typeof status); setRole(user.role); }, [user]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await action.run("profile", async () => {
      await apiRequest(`/api/v1/admin/users/${user.id}`, { method: "PATCH", body: JSON.stringify({ fullName, kycStatus, status, role }) });
      await invalidate();
      return "Perfil actualizado.";
    });
  };
  return (
    <form className="card admin-form" onSubmit={submit}>
      <div className="section-heading"><div><span className="page-eyebrow">Perfil</span><h2>Datos del cliente</h2></div><span>Alta {formatTimestamp(user.createdAt)}</span></div>
      <Field label="Nombre completo" htmlFor="u-name"><input id="u-name" className="standalone-input" value={fullName} onChange={(event) => setFullName(event.target.value)} minLength={2} required /></Field>
      <div className="field-grid three">
        <Field label="Verificación (KYC)" htmlFor="u-kyc">
          <select id="u-kyc" className="standalone-input" value={kycStatus} onChange={(event) => setKyc(event.target.value as typeof kycStatus)}>
            <option value="pending">Pendiente</option><option value="verified">Verificada</option><option value="rejected">Rechazada</option>
          </select>
        </Field>
        <Field label="Estado de la cuenta" htmlFor="u-status">
          <select id="u-status" className="standalone-input" value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
            <option value="active">Activa</option><option value="suspended">Suspendida</option>
          </select>
        </Field>
        <Field label="Rol" htmlFor="u-role">
          <select id="u-role" className="standalone-input" value={role} onChange={(event) => setRole(event.target.value as typeof role)}>
            <option value="customer">Cliente</option><option value="agent">Agente</option><option value="admin">Administrador</option><option value="lender">Aliado financiero (Peso a Peso)</option>
          </select>
        </Field>
      </div>
      <div className="meta-row"><span>Correo {user.emailVerified ? "verificado" : "sin verificar"}</span><span>{user.speiAccounts} cuenta(s) SPEI</span><span>{user.hasPassword ? "Con contraseña" : "Solo acceso social"}</span></div>
      <Feedback error={action.error} notice={action.notice} />
      <button className="primary-button solid" type="submit" disabled={action.busy !== null}>{action.busy === "profile" ? <Spinner /> : null} Guardar perfil</button>
    </form>
  );
}

function TripForm({ data }: { data: AdminUserDetail }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const trip = data.trip;
  const [destination, setDestination] = useState(trip?.destination ?? "");
  const [departureDate, setDeparture] = useState(trip?.departureDate ?? "");
  const [finalPaymentDate, setFinalPayment] = useState(trip?.finalPaymentDate ?? "");
  const [contribution, setContribution] = useState(trip?.rateLockContribution?.toString() ?? "");
  const [notes, setNotes] = useState(trip?.notes ?? "");
  useEffect(() => {
    setDestination(trip?.destination ?? ""); setDeparture(trip?.departureDate ?? ""); setFinalPayment(trip?.finalPaymentDate ?? "");
    setContribution(trip?.rateLockContribution?.toString() ?? ""); setNotes(trip?.notes ?? "");
  }, [trip]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await action.run("trip", async () => {
      await apiRequest(`/api/v1/admin/users/${data.user.id}/trip`, {
        method: "PUT",
        body: JSON.stringify({
          destination: destination.trim() || null,
          departureDate: departureDate || null,
          finalPaymentDate: finalPaymentDate || null,
          rateLockContribution: contribution === "" ? null : Number(contribution),
          notes: notes.trim() || null
        })
      });
      await invalidate();
      return "Términos del viaje actualizados. El cliente los ve en Inicio, Wallet y Perfil.";
    });
  };
  const saved = data.wallets.find((wallet) => wallet.product === "libre")?.balance.available ?? 0;
  const contributionNumber = Number(contribution);
  return (
    <form className="card admin-form" onSubmit={submit}>
      <div className="section-heading"><div><span className="page-eyebrow">Viaje</span><h2>Términos del viaje</h2></div>{trip && <span>Actualizado {formatTimestamp(trip.updatedAt)}</span>}</div>
      <Field label="Destino" htmlFor="t-dest" hint="Aparece como título de los hitos del viaje."><input id="t-dest" className="standalone-input" value={destination} onChange={(event) => setDestination(event.target.value)} maxLength={120} placeholder="Luna de miel en Maldivas" /></Field>
      <div className="field-grid">
        <Field label="Fecha de salida" htmlFor="t-departure"><input id="t-departure" type="date" className="standalone-input" value={departureDate} onChange={(event) => setDeparture(event.target.value)} /></Field>
        <Field label="Fecha de pago final" htmlFor="t-payment" hint="Debe ser anterior a la salida."><input id="t-payment" type="date" className="standalone-input" value={finalPaymentDate} onChange={(event) => setFinalPayment(event.target.value)} /></Field>
      </div>
      <Field label="Aportación para asegurar la tarifa (MXN)" htmlFor="t-contrib" hint={contribution && Number.isFinite(contributionNumber) && contributionNumber > 0 ? `El cliente lleva ${money.format(saved)} ahorrados · ${Math.min(100, Math.round((saved / contributionNumber) * 100))}% cubierto` : "Monto que el cliente debe tener en su wallet para asegurar la tarifa cotizada."}>
        <input id="t-contrib" className="standalone-input" inputMode="decimal" value={contribution} onChange={(event) => setContribution(event.target.value.replace(/[^\d.]/g, ""))} placeholder="12000" />
      </Field>
      <Field label="Notas para el cliente" htmlFor="t-notes"><textarea id="t-notes" className="standalone-input textarea" value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={500} rows={3} placeholder="Incluye traslados y seguro de viaje." /></Field>
      <Feedback error={action.error} notice={action.notice} />
      <button className="primary-button solid" type="submit" disabled={action.busy !== null}>{action.busy === "trip" ? <Spinner /> : null} Guardar términos del viaje</button>
    </form>
  );
}

/** The customer's Kuspit link: identity, contract, token state and the two repairs an admin can run. */
function KuspitCard({ data }: { data: AdminUserDetail }) {
  const { user, kuspit, wallets } = data;
  const invalidate = useInvalidateAll();
  const action = useAction();
  const tokenExpiry = kuspit?.tokenExpiresAt ? new Date(kuspit.tokenExpiresAt).getTime() : null;
  const tokenState = tokenExpiry === null ? "sin token" : tokenExpiry < Date.now() ? "vencido" : tokenExpiry < Date.now() + 2 * 3_600_000 ? "por renovar" : "vigente";
  const relogin = () => action.run("relogin", async () => {
    const result = await apiRequest<{ data: { contrato: string | null; clabe: string | null; clabeRecorded: boolean } }>(`/api/v1/admin/kuspit/identities/${user.id}/relogin`, { method: "POST" });
    await invalidate();
    return result.data.contrato ? `Kuspit reporta el contrato ${result.data.contrato}${result.data.clabe ? ` con CLABE ${result.data.clabe}` : ""}${result.data.clabeRecorded ? " (registrada en la wallet)" : ""}.` : "Revinculación exitosa; Kuspit aún no reporta un contrato.";
  });
  const synchronize = (walletId: string) => action.run(`sync-${walletId}`, async () => {
    const result = await apiRequest<{ data: { providerBalance: number; asOf: string; changed: boolean } }>(`/api/v1/admin/kuspit/wallets/${walletId}/synchronize`, { method: "POST" });
    await invalidate();
    return `Kuspit reporta ${money.format(result.data.providerBalance)} al ${formatTimestamp(result.data.asOf)}${result.data.changed ? "" : " (sin cambios)"}.`;
  });
  const provision = (walletId: string) => action.run(`provision-${walletId}`, async () => {
    const result = await apiRequest<{ data: { status: string; accountRef: string; clabe: string | null } }>(`/api/v1/admin/kuspit/wallets/${walletId}/provision`, { method: "POST" });
    await invalidate();
    return result.data.status === "active" ? `Contrato ${result.data.accountRef} abierto${result.data.clabe ? ` · CLABE ${result.data.clabe}` : ""}.` : `Kuspit registró la solicitud; el contrato ${result.data.accountRef} sigue pendiente.`;
  });
  return (
    <section className="card">
      <div className="section-heading"><div><span className="page-eyebrow">Kuspit</span><h2>Identidad y contrato</h2></div><Link to={`/admin/kuspit/contratos?q=${encodeURIComponent(user.email)}`} className="text-link"><Building2 size={14} aria-hidden="true" /> Ver en Kuspit</Link></div>
      {!kuspit && <p className="field-hint">Este cliente aún no está vinculado con Kuspit. La vinculación ocurre al abrir su primera wallet.</p>}
      {kuspit && (
        <KeyValues rows={[
          { label: "idExterno", value: kuspit.idExterno, mono: true },
          { label: "Usuario Kuspit", value: kuspit.usuario ?? "—", mono: true },
          { label: "Contrato", value: kuspit.contrato ?? "sin contrato", mono: true },
          { label: "Token", value: <Pill tone={tokenState === "vigente" ? "ok" : tokenState === "por renovar" ? "warn" : "bad"} label={`${tokenState}${kuspit.tokenExpiresAt ? ` · vence ${formatTimestamp(kuspit.tokenExpiresAt)}` : ""}`} /> },
          { label: "Vinculado", value: formatTimestamp(kuspit.linkedAt) },
          kuspit.tokenRefreshedAt ? { label: "Última renovación", value: formatTimestamp(kuspit.tokenRefreshedAt) } : null
        ]} />
      )}
      {wallets.length > 0 && (
        <div className="admin-stack tight" style={{ marginTop: 14 }}>
          {wallets.map((wallet) => (
            <div key={wallet.id} className="wallet-sync-row">
              <div className="table-primary">
                <strong>{wallet.product === "libre" ? "Wallet Libre" : "Wallet Aventuria"} · <span translate="no">{money.format(wallet.balance.available)}</span></strong>
                <span>{wallet.providerAccountRef ? `Contrato ${wallet.providerAccountRef}` : "Sin contrato"} · {wallet.balance.source === "kuspit" && wallet.synchronization.lastSyncedAt ? `saldo de Kuspit al ${formatTimestamp(wallet.synchronization.lastSyncedAt)}` : "Kuspit no ha reportado saldo"}{wallet.synchronization.lastError ? ` · última consulta falló (${wallet.synchronization.lastError})` : wallet.synchronization.stale ? " · desactualizado" : ""}</span>
              </div>
              {wallet.providerAccountRef && wallet.status !== "closed" && (
                <div className="row-actions">
                  <button type="button" className="secondary-button small" onClick={() => void synchronize(wallet.id)} disabled={action.busy !== null}>
                    {action.busy === `sync-${wallet.id}` ? <Spinner /> : <RefreshCw size={13} aria-hidden="true" />} Actualizar saldo
                  </button>
                </div>
              )}
              {!wallet.providerAccountRef && wallet.status !== "closed" && (
                <button type="button" className="secondary-button small" onClick={() => void provision(wallet.id)} disabled={action.busy !== null}>
                  {action.busy === `provision-${wallet.id}` ? <Spinner /> : null} Reintentar apertura
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <Feedback error={action.error} notice={action.notice} />
      {kuspit && (
        <div className="inline-actions" style={{ marginTop: 12 }}>
          <button type="button" className="link-button" onClick={() => void relogin()} disabled={action.busy !== null}>{action.busy === "relogin" ? "Revinculando…" : "Revincular con Kuspit (descubre contrato y CLABE)"}</button>
        </div>
      )}
      <DestinationsBlock destinations={data.speiDestinations} />
    </section>
  );
}

const DESTINATION_LABEL: Record<AdminSpeiDestination["kuspitStatus"], { label: string; tone: "ok" | "warn" | "bad" | "info" | "muted" }> = {
  not_submitted: { label: "sin enviar", tone: "muted" },
  pending: { label: "en revisión", tone: "warn" },
  authorized: { label: "autorizada", tone: "ok" },
  rejected: { label: "rechazada", tone: "bad" },
  replaced: { label: "reemplazada", tone: "muted" }
};

/**
 * The customer's withdrawal destination as Kuspit sees it. Kuspit authorises a registered CLABE
 * by hand and tells nobody (sandbox: it answers on the support ticket), so this is where an admin
 * records that answer. A pending account also flips to authorised on its own when a withdrawal to
 * it is accepted.
 */
function DestinationsBlock({ destinations }: { destinations: AdminSpeiDestination[] }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [note, setNote] = useState("");
  const decide = (destination: AdminSpeiDestination, status: "authorized" | "rejected") => action.run(`destination-${destination.id}-${status}`, async () => {
    await apiRequest(`/api/v1/admin/kuspit/destinations/${destination.id}/status`, {
      method: "POST", body: JSON.stringify({ status, ...(note.trim() ? { note: note.trim() } : {}) })
    });
    await invalidate();
    setNote("");
    return status === "authorized"
      ? `Cuenta ${destination.clabe} marcada como autorizada; el cliente fue avisado.`
      : `Cuenta ${destination.clabe} marcada como rechazada; el cliente puede registrar otra.`;
  });
  if (destinations.length === 0) return null;
  return (
    <div className="admin-stack tight" style={{ marginTop: 18 }}>
      <div className="section-heading"><div><span className="page-eyebrow">Retiros</span><h3>Cuenta destino en Kuspit</h3></div></div>
      <p className="field-hint">
        Kuspit acepta una sola cuenta destino por contrato, registrada con su carátula y autorizada manualmente (minutos, máximo 72 h, sin aviso).
        Cuando Kuspit confirme la autorización o el rechazo en el ticket, regístralo aquí.
      </p>
      {destinations.map((destination) => (
        <div key={destination.id} className="wallet-sync-row">
          <div className="table-primary">
            <strong><span translate="no">{destination.clabe}</span>{destination.label ? ` · ${destination.label}` : ""} <Pill tone={DESTINATION_LABEL[destination.kuspitStatus].tone} label={DESTINATION_LABEL[destination.kuspitStatus].label} /></strong>
            <span>
              {destination.contract ? `Contrato ${destination.contract} · ` : ""}
              {destination.action === "change" ? "cambio de cuenta" : destination.action === "register" ? "alta de cuenta" : "no enviada a Kuspit"}
              {destination.submittedAt ? ` · enviada ${formatTimestamp(destination.submittedAt)}` : ""}
              {destination.authorizedAt ? ` · autorizada ${formatTimestamp(destination.authorizedAt)}` : ""}
              {destination.documentName ? ` · ${destination.documentName}` : ""}
              {destination.message ? ` · ${destination.message}` : ""}
            </span>
          </div>
          {destination.kuspitStatus === "pending" && (
            <div className="row-actions">
              <button type="button" className="secondary-button small" onClick={() => void decide(destination, "authorized")} disabled={action.busy !== null}>
                {action.busy === `destination-${destination.id}-authorized` ? <Spinner /> : null} Kuspit autorizó
              </button>
              <button type="button" className="secondary-button small danger" onClick={() => void decide(destination, "rejected")} disabled={action.busy !== null}>
                {action.busy === `destination-${destination.id}-rejected` ? <Spinner /> : null} Kuspit rechazó
              </button>
            </div>
          )}
        </div>
      ))}
      {destinations.some((destination) => destination.kuspitStatus === "pending") && (
        <Field label="Nota de Kuspit (opcional)" htmlFor="destination-note" hint="Se guarda en la bitácora y, en un rechazo, se incluye en el correo al cliente.">
          <input id="destination-note" className="standalone-input" value={note} onChange={(event) => setNote(event.target.value)} maxLength={300} />
        </Field>
      )}
      <Feedback error={action.error} notice={action.notice} />
    </div>
  );
}

/** Staff and partner accounts sign in with an authenticator code; a lost phone is reset here. */
function StaffMfaCard({ data }: { data: AdminUserDetail }) {
  const { user } = data;
  const invalidate = useInvalidateAll();
  const enrolledAt = user.mfaEnrolledAt ?? null;
  return (
    <section className="card">
      <div className="section-heading"><div><span className="page-eyebrow">Seguridad</span><h2>Verificación en dos pasos</h2></div>
        <Pill tone={enrolledAt ? "ok" : "warn"} label={enrolledAt ? "Activa" : "Sin configurar"} /></div>
      <p className="field-hint">
        {enrolledAt
          ? `Configurada el ${formatTimestamp(enrolledAt)}. Restablécela solo si la persona perdió su teléfono y sus códigos de recuperación; tendrá que configurarla de nuevo al entrar.`
          : "Se le pedirá configurar su aplicación autenticadora la próxima vez que entre a la administración o al portal de aliados."}
      </p>
      {enrolledAt && (
        <div className="danger-actions">
          <ConfirmButton
            label="Restablecer verificación" icon={<KeyRound size={15} aria-hidden="true" />}
            title="Restablecer verificación en dos pasos"
            message={<p>Se borrará el autenticador de <strong>{user.fullName}</strong> ({user.email}) y sus códigos de recuperación. Confirma su identidad por otro medio antes de hacerlo. Queda registrado en la bitácora.</p>}
            confirmLabel="Restablecer"
            onConfirm={async () => { await apiRequest(`/api/v1/admin/users/${user.id}/mfa`, { method: "DELETE" }); await invalidate(); }}
          />
        </div>
      )}
    </section>
  );
}

function UserDangerZone({ data }: { data: AdminUserDetail }) {
  const { user } = data;
  const invalidate = useInvalidateAll();
  const navigate = useNavigate();
  const hasFunds = data.wallets.some((wallet) => wallet.balance.available > 0.01);
  const hasContract = data.wallets.some((wallet) => Boolean(wallet.providerAccountRef)) || Boolean(data.kuspit?.contrato);
  const alreadyClosed = user.status === "closed";
  return (
    <section className="card admin-danger">
      <div className="section-heading"><div><span className="page-eyebrow">Zona de riesgo</span><h2>Eliminar cuenta</h2></div></div>
      <p className="field-hint">Cerrar la cuenta impide el acceso y congela las wallets, pero conserva el historial (reversible). Si el cliente tiene contrato Kuspit, el cierre pasa por Kuspit (abajo). Eliminarla borra todo de forma permanente.</p>
      <div className="danger-actions">
        <ConfirmButton
          label="Cerrar cuenta" icon={<Ban size={15} aria-hidden="true" />}
          disabled={alreadyClosed || hasFunds || hasContract}
          disabledHint={alreadyClosed ? "La cuenta ya está cerrada." : hasFunds ? "La cuenta conserva saldo; resuelve los fondos primero." : "Tiene contrato Kuspit: usa \"Cerrar contrato Kuspit\"."}
          title="Cerrar esta cuenta"
          message={<p>La cuenta de <strong>{user.fullName}</strong> ({user.email}) no podrá iniciar sesión y sus wallets quedarán congeladas. Es reversible desde el estado de la cuenta.</p>}
          confirmLabel="Cerrar cuenta"
          onConfirm={async () => { await apiRequest(`/api/v1/admin/users/${user.id}`, { method: "DELETE" }); await invalidate(); }}
        />
        <ConfirmButton
          danger label="Eliminar permanentemente" icon={<Trash2 size={15} aria-hidden="true" />}
          disabled={hasContract || hasFunds}
          disabledHint={hasContract ? "Tiene contrato Kuspit: Kuspit conserva el contrato por ley, así que la cuenta nunca se elimina." : hasFunds ? "Conserva saldo; resuelve los fondos primero." : undefined}
          title="Eliminar esta cuenta"
          message={<p>Se borrará <strong>{user.fullName}</strong> ({user.email}) y todo su historial (wallets, movimientos, identidad Kuspit) de forma <strong>permanente e irreversible</strong>.</p>}
          confirmLabel="Eliminar para siempre"
          onConfirm={async () => { await apiRequest(`/api/v1/admin/users/${user.id}/purge`, { method: "DELETE" }); await invalidate(); navigate("/admin/usuarios"); }}
        />
      </div>
      {hasContract && <KuspitClosureCard userId={user.id} />}
    </section>
  );
}

function WalletForm({ wallet }: { wallet: Wallet }) {
  const invalidate = useInvalidateAll();
  const action = useAction();
  const [goalTitle, setGoalTitle] = useState(wallet.goal?.title ?? "");
  const [goalAmount, setGoalAmount] = useState(wallet.goal?.amount?.toString() ?? "");
  const [goalDate, setGoalDate] = useState(wallet.goal?.date ?? "");
  const [goalPhotoId, setGoalPhotoId] = useState<string | null>(wallet.goal?.photoId ?? null);
  const [status, setStatus] = useState(wallet.status);
  useEffect(() => {
    setGoalTitle(wallet.goal?.title ?? ""); setGoalAmount(wallet.goal?.amount?.toString() ?? ""); setGoalDate(wallet.goal?.date ?? "");
    setGoalPhotoId(wallet.goal?.photoId ?? null); setStatus(wallet.status);
  }, [wallet]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    await action.run(wallet.id, async () => {
      const body: Record<string, unknown> = {
        goalTitle: goalTitle.trim() || null,
        goalAmount: goalAmount === "" ? null : Number(goalAmount),
        goalDate: goalDate || null,
        goalPhotoId
      };
      if ((status === "active" || status === "suspended") && status !== wallet.status) body.status = status;
      await apiRequest<ApiEnvelope<{ wallet: Wallet }>>(`/api/v1/admin/wallets/${wallet.id}`, { method: "PATCH", body: JSON.stringify(body) });
      await invalidate();
      return "Wallet actualizada. El cliente ve la nueva meta de inmediato.";
    });
  };
  return (
    <form className="card admin-form" onSubmit={submit}>
      <div className="section-heading">
        <div><span className="page-eyebrow">Wallet {wallet.product === "libre" ? "Libre" : "Aventuria"}</span><h2 translate="no">{money.format(wallet.balance.available)}</h2></div>
        <span className={`tag ${wallet.status}`}>{wallet.status}</span>
      </div>
      <div className="meta-row">
        <span>Interés {money.format(wallet.balance.accruedInterest)}</span>
        <span>Por confirmar {money.format(wallet.balance.pendingIn)}</span>
        <span>Contrato {wallet.providerAccountRef ?? "pendiente"}</span>
        <span>Alta {formatCalendarDate(wallet.createdAt.slice(0, 10))}</span>
      </div>
      <Field label="Título del sueño" htmlFor={`w-title-${wallet.id}`}><input id={`w-title-${wallet.id}`} className="standalone-input" value={goalTitle} onChange={(event) => setGoalTitle(event.target.value)} maxLength={120} /></Field>
      <div className="field-grid">
        <Field label="Monto meta (MXN)" htmlFor={`w-amount-${wallet.id}`} hint={wallet.goal?.progress !== null && wallet.goal?.progress !== undefined ? `${Math.round(wallet.goal.progress * 100)}% alcanzado` : undefined}>
          <input id={`w-amount-${wallet.id}`} className="standalone-input" inputMode="decimal" value={goalAmount} onChange={(event) => setGoalAmount(event.target.value.replace(/[^\d.]/g, ""))} />
        </Field>
        <Field label="Fecha objetivo" htmlFor={`w-date-${wallet.id}`}><input id={`w-date-${wallet.id}`} type="date" className="standalone-input" value={goalDate} onChange={(event) => setGoalDate(event.target.value)} /></Field>
      </div>
      <AdminPhotoField kind="dream" value={goalPhotoId} onChange={setGoalPhotoId} label="Foto del sueño" />
      {(wallet.status === "active" || wallet.status === "suspended") && (
        <Field label="Estado de la wallet" htmlFor={`w-status-${wallet.id}`}>
          <select id={`w-status-${wallet.id}`} className="standalone-input" value={status} onChange={(event) => setStatus(event.target.value as Wallet["status"])}>
            <option value="active">Activa</option><option value="suspended">Suspendida</option>
          </select>
        </Field>
      )}
      <Feedback error={action.error} notice={action.notice} />
      <button className="primary-button solid" type="submit" disabled={action.busy !== null}>{action.busy === wallet.id ? <Spinner /> : null} Guardar wallet</button>
      <div className="danger-actions wallet-danger">
        <ConfirmButton
          label="Cerrar wallet" icon={<Ban size={14} aria-hidden="true" />}
          disabled={wallet.status === "closed" || wallet.balance.available > 0.01}
          disabledHint={wallet.status === "closed" ? "La wallet ya está cerrada." : "Conserva saldo disponible; resuelve los fondos primero."}
          title="Cerrar esta wallet"
          message={<p>La wallet quedará cerrada y el cliente no podrá operarla. Es reversible.</p>}
          confirmLabel="Cerrar wallet"
          onConfirm={async () => { await apiRequest(`/api/v1/admin/wallets/${wallet.id}`, { method: "DELETE" }); await invalidate(); }}
        />
        <ConfirmButton
          danger label="Eliminar wallet" icon={<Trash2 size={14} aria-hidden="true" />}
          disabled={Boolean(wallet.providerAccountRef) || wallet.balance.available > 0.01}
          disabledHint={wallet.providerAccountRef ? "Tiene un contrato Kuspit; ciérrala en su lugar." : "Conserva saldo; resuelve los fondos primero."}
          title="Eliminar esta wallet"
          message={<p>Se borrará la wallet y su historial de forma <strong>permanente</strong>. Solo disponible para wallets sin contrato Kuspit ni saldo.</p>}
          confirmLabel="Eliminar para siempre"
          onConfirm={async () => { await apiRequest(`/api/v1/admin/wallets/${wallet.id}/purge`, { method: "DELETE" }); await invalidate(); }}
        />
      </div>
    </form>
  );
}
